/**
 * Router + auth context. The flow is the product:
 *   Landing → Register (Google/Apple/email start) → the SAME setup wizard for
 *   everyone (consent → card → banks → payday → first-month import) → Dashboard.
 * A social login never skips setup; `onboarding_complete` from /auth/me is the
 * single routing truth. Paywall route catches expired trials.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api, tokens } from './api.js';
import { AskHost } from './components/Ask.jsx';
import AuthPage from './pages/AuthPage.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Landing from './pages/Landing.jsx';
import AndroidTesters from './pages/AndroidTesters.jsx';
import LandingNew from './pages/Landing.new.jsx';   // Nocturne homepage redesign — preview at /home-preview
import GlobalWaitlist from './pages/GlobalWaitlist.jsx';   // vaultpayday.com home page
import { siteFor, setAccountRegion, taxPackAvailable } from './global.js';
import { configureMoney } from './money.js';
import { earlyAccess } from './early.js';
import Onboarding from './pages/Onboarding.jsx';
import Paywall from './pages/Paywall.jsx';
import GoLiveOverlay from './components/GoLiveOverlay.jsx';
import { AIConsentHost } from './components/AIGate.jsx';
import Support from './pages/Support.jsx';
import { Privacy, Terms, DataDeletion } from './pages/Legal.jsx';
import Manage from './pages/Manage.jsx';
import Insights from './pages/Insights.jsx';
import Account from './pages/Account.jsx';
import Revenue from './pages/Revenue.jsx';
import Scope from './pages/Scope.jsx';
import ScopeWizard, { ResultsReport } from './pages/ScopeWizard.jsx';
import MoneySetup from './pages/MoneySetup.jsx';
import TaxPack from './pages/TaxPack.jsx';
import Reporter from './components/Reporter.jsx';
import Guide from './components/Guide.jsx';
import { EditProvider } from './components/SiteEdit.jsx';
import Splash, { hasLastMonth } from './components/Splash.jsx';
import { OpenRowProvider } from './components/AccordionRegister.jsx';
import SimpleAccount from './components/simple/SimpleAccount.jsx';
import { ViewToastHost } from './components/simple/viewMode.jsx';

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export default function App() {
  const [me, setMe] = useState(undefined);   // undefined = loading, null = anonymous
  const [boot, setBoot] = useState(null);    // null | 'offline' | 'error' — designed wait states

  const reload = useCallback(async () => {
    if (!tokens.access && !tokens.refresh) return setMe(null);
    setBoot(null);
    try {
      const m = await api('/auth/me/');
      setMe(m);
      // Cache identity so "Open my last month" has something to open on a
      // later cold load that can't reach the network.
      try { localStorage.setItem('vault_last_me', JSON.stringify(m)); } catch { /* private mode */ }
    } catch (e) {
      // A genuinely dead session goes home to sign in again. A network or
      // server stall does NOT dump the user to login — it shows the designed
      // offline / failure screen, because their month is safe on the device.
      if (String((e && e.message) || '') === 'Session expired') return setMe(null);
      setBoot(navigator.onLine === false ? 'offline' : 'error');
    }
  }, []);

  // Open the last synced month held on this device (from the cold-splash
  // deadline card, the offline screen, or the failure screen).
  const openLast = useCallback(() => {
    try {
      const m = JSON.parse(localStorage.getItem('vault_last_me') || 'null');
      if (m) { setBoot(null); return setMe({ ...m, _cached: true }); }  // view-only restore
    } catch { /* ignore */ }
    reload();
  }, [reload]);

  useEffect(() => { reload(); }, [reload]);

  // Simple view (#476) lives on the account: re-read /auth/me when the tab
  // comes back, so a switch made on the phone or watch lands here too.
  useEffect(() => {
    const sync = () => {
      if (document.visibilityState !== 'visible' || (!tokens.access && !tokens.refresh)) return;
      api('/auth/me/').then((m) => setMe((cur) => (cur ? { ...cur, ...m } : cur))).catch(() => {});
    };
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => { window.removeEventListener('focus', sync); document.removeEventListener('visibilitychange', sync); };
  }, []);

  // Capture an invite code from ?ref= once, so it survives the hop from a
  // marketing link (/?ref=CODE) to the register screen. Cleared on signup.
  useEffect(() => {
    const r = new URLSearchParams(window.location.search).get('ref');
    if (r) { try { sessionStorage.setItem('vault_ref', r.trim().slice(0, 16)); } catch { /* ignore */ } }
  }, []);

  const logout = () => {
    tokens.clear();
    // Also drop the cached identity — otherwise the splash 'Open my last month'
    // path (openLast) can restore a signed-out owner as `me`, lighting up the
    // app + staff affordances (Guide, Reporter, Edit site) with no real session.
    try { localStorage.removeItem('vault_last_me'); } catch { /* private mode */ }
    setMe(null);
  };
  // Guide + Reporter belong to the signed-in APP, never the marketing / coming-soon
  // pages — gate them to app routes so the public home stays clean even if an
  // identity leaks back in.
  const { pathname } = useLocation();
  const inApp = /^\/(app|manage|insights|account|tax|money|testing|support|revenue)(\/|$)/.test(pathname);
  // A tester moved to live answers the go-live overlay before anything else in
  // the app works; the public marketing and legal pages stay readable.
  // Money and the tax pack follow the signed-in account's country and currency.
  // Set here, during render and before any child renders, so every amount on
  // the first paint already reads in the account's own currency.
  configureMoney(me || undefined);
  setAccountRegion(me);
  const goLiveBlocks = !!me?.golive_confirm
    && !/^\/(privacy|terms|delete-account|home-preview|android-testers|billing\/callback)?$/.test(pathname);

  // Design QA: preview any boot state at any viewport via ?splash=stuck|offline|error
  // (renders only the splash screen — no data, no side effects).
  const _sp = new URLSearchParams(window.location.search).get('splash');
  if (_sp && ['splash', 'stuck', 'offline', 'error'].includes(_sp)) {
    return <Splash view={_sp} onRetry={reload} onOpenLast={openLast} hasLast={hasLastMonth()} />;
  }

  if (me === undefined) {
    return <Splash view={boot || 'splash'} onRetry={reload} onOpenLast={openLast} hasLast={hasLastMonth()} />;
  }

  return (
    <AuthCtx.Provider value={{ me, reload, logout, setMe }}>
      <EditProvider>
      <OpenRowProvider>
      <Routes>
        {/* The home page is for EVERYONE — being signed in must never hide
            the marketing site. The app lives behind /app. */}
        {/* One platform, two domains: vaultpayday.com gets the global waitlist,
            vault-app.co.za keeps the South African home page. */}
        <Route path="/" element={siteFor(window.location) === 'global' ? <GlobalWaitlist /> : <Landing />} />
        {/* Pre-launch: sign-in / sign-up are hidden from the public unless the
            visitor arrived via the direct ?access= link (see early.js). Already
            signed-in users always route through the Gate. NOT public yet.
            Each AuthPage route carries a distinct key: all three render the SAME
            component, so without one React reuses the instance across /login ->
            /register and AuthPage's `view` state — seeded from `mode` by useState
            and never resynced — stays on the old form. That made "New here? Start
            free" change the URL while still showing the sign-in form. */}
        <Route path="/login" element={me ? <Gate /> : <AuthPage key="login" mode="login" />} />
        <Route path="/register" element={me ? <Gate /> : <AuthPage key="register" mode="register" />} />
        <Route path="/reset" element={me ? <Gate /> : <AuthPage key="reset" mode="reset" />} />
        <Route path="/setup" element={me ? <Onboarding /> : <Navigate to="/register" />} />
        <Route path="/paywall" element={me ? <Paywall /> : <Navigate to="/login" />} />
        <Route path="/app/*" element={me ? <Gate app /> : <Navigate to="/login" />} />
        <Route path="/manage" element={me ? <Manage /> : <Navigate to="/login" />} />
        <Route path="/insights" element={me ? <Insights /> : <Navigate to="/login" />} />
        <Route path="/support" element={me?.is_superuser ? <Support /> : <Navigate to="/" />} />
        <Route path="/revenue" element={me?.is_superuser ? <Revenue /> : <Navigate to="/" />} />
        <Route path="/account" element={me ? (me.view_mode === 'simple' ? <SimpleAccount /> : <Account />) : <Navigate to="/login" />} />
        <Route path="/tax" element={me ? (taxPackAvailable() ? <TaxPack /> : <Navigate to="/app" />) : <Navigate to="/login" />} />
        <Route path="/testing" element={me ? <ScopeWizard /> : <Navigate to="/login" />} />
        {/* Hub standard: /scope IS the scope page — the full board + worth +
            wizard + three-level notes for signed-in staff; §4 keeps it reachable
            signed-out, where Scope shows only the sign-in prompt. */}
        <Route path="/scope" element={me?.is_superuser ? <ScopeWizard /> : <Scope />} />
        {/* Rollout §10: the report matrix at its standard URL. */}
        <Route path="/internal/scope-tests/report"
               element={me?.is_superuser ? <MatrixReport /> : <Scope />} />
        <Route path="/money" element={me ? <MoneySetup /> : <Navigate to="/login" />} />
        <Route path="/billing/callback" element={<BillingCallback />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/delete-account" element={<DataDeletion />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/android-testers" element={<AndroidTesters />} />
        <Route path="/home-preview" element={<LandingNew />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      {goLiveBlocks && <GoLiveOverlay />}
      {me && <AIConsentHost />}
      {me && inApp && !goLiveBlocks && <Reporter />}
      {/* Simple view keeps its own 4-step guide; the tour launcher and its nudge stay off. */}
      {me && inApp && !goLiveBlocks && <Guide simple={me.view_mode === 'simple'} />}
      <AskHost />
      <ViewToastHost />
      </OpenRowProvider>
      </EditProvider>
    </AuthCtx.Provider>
  );
}

/** Rollout §10/§11 wrapper: the superuser RESULTS REPORT on its standard URL —
 * every test's pass/fail plus its comment thread, printable/exportable. */
function MatrixReport() {
  return (
    <div className="min-h-full max-w-4xl mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-5 no-print">
        <img src="/logo.svg" alt="" className="w-9 h-9" />
        <h1 className="text-xl font-extrabold bg-vault-gradient bg-clip-text
                       text-transparent">
          Scope results report
        </h1>
      </div>
      <ResultsReport />
      <style>{`@media print{.no-print{display:none!important}}`}</style>
    </div>
  );
}

/** Paystack sends the browser here after card capture (web AND the mobile
 * app's browser flow). Verify by reference, then route home / tell the mobile
 * user to hop back to the app. */
function BillingCallback() {
  const [msg, setMsg] = useState('Confirming your card with Paystack…');
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('reference');
    if (!ref) { setMsg('No payment reference found.'); return; }
    api('/billing/verify-capture/', { method: 'POST', body: { reference: ref } })
      .then(() => setMsg('Card added ✓ — you can close this page and return to the Vault app, or continue here.'))
      .catch((e) => setMsg(e.data?.detail || 'Could not confirm the card — try again from setup.'));
  }, []);
  return (
    <div className="min-h-full grid place-items-center px-6 text-center">
      <div className="card max-w-md">
        <img src="/logo.svg" alt="" className="w-12 h-12 mx-auto mb-4" />
        <p>{msg}</p>
        <a href="/app" className="btn-primary inline-block mt-5">Open Vault</a>
      </div>
    </div>
  );
}

/** Single routing truth: setup not done → wizard; no access → paywall; else app. */
function Gate({ app = false }) {
  const { me } = useAuth();
  const loc = useLocation();
  if (!me.onboarding_complete) return <Navigate to="/setup" state={{ from: loc }} />;
  // The go-live overlay (rendered by App) sits over an empty page until answered.
  if (me.golive_confirm) return <div className="min-h-full" />;
  if (!me.has_access) return <Navigate to="/paywall" />;
  return app ? <Dashboard /> : <Navigate to="/app" />;
}
